import React, { useState } from "react";
import PropTypes from "prop-types";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import { Form, Formik } from "formik";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import {
  mainBonusFields,
  progressiveBonusValidationSchema,
  quadRanks,
  straightFlushFields,
} from "../progressiveBonusConfig";
import { downloadProgressiveBonusesImage } from "../downloadProgressiveBonusesImage";

function AmountField({ field, label, values, errors, touched, handleBlur, handleChange }) {
  return (
    <label className="progressive-bonuses-editor__field">
      <MDTypography component="span" variant="button" fontWeight="medium" color="text">
        {label}
      </MDTypography>
      <div className="progressive-bonuses-editor__amount-input">
        <span aria-hidden="true">$</span>
        <input
          aria-label={`${label} amount`}
          id={field}
          name={field}
          type="number"
          min="0"
          step="0.01"
          value={values[field]}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </div>
      {touched[field] && errors[field] ? <small role="alert">{errors[field]}</small> : null}
    </label>
  );
}

AmountField.propTypes = {
  field: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  values: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  errors: PropTypes.objectOf(PropTypes.string).isRequired,
  touched: PropTypes.objectOf(PropTypes.bool).isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
};

function ProgressiveBonusesEditorForm({ bonuses, onSubmit, onDownload }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  const handleDownload = async (values) => {
    setDownloadError("");
    setIsDownloading(true);

    try {
      await onDownload(values);
    } catch (error) {
      setDownloadError(error.message || "Unable to download the image.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={bonuses}
      validationSchema={progressiveBonusValidationSchema}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, handleBlur, handleChange, isSubmitting }) => (
        <Form className="progressive-bonuses-editor__form">
          <div className="progressive-bonuses-editor__section">
            <MDTypography component="h2" variant="h6" color="white">
              Main bonuses
            </MDTypography>
            <div className="progressive-bonuses-editor__grid">
              {mainBonusFields.map((bonus) => (
                <AmountField
                  key={bonus.field}
                  field={bonus.field}
                  label={bonus.label}
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                />
              ))}
            </div>
          </div>

          <div className="progressive-bonuses-editor__section">
            <MDTypography component="h2" variant="h6" color="white">
              Straight flush bonuses
            </MDTypography>
            <div className="progressive-bonuses-editor__grid">
              {straightFlushFields.map((bonus) => (
                <AmountField
                  key={bonus.field}
                  field={bonus.field}
                  label={bonus.label}
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                />
              ))}
            </div>
          </div>

          <label className="progressive-bonuses-editor__field progressive-bonuses-editor__rank-field">
            <MDTypography component="span" variant="button" fontWeight="medium" color="text">
              Quad of the Day rank
            </MDTypography>
            <select
              aria-label="Quad of the Day rank"
              id="quad_of_day_rank"
              name="quad_of_day_rank"
              value={values.quad_of_day_rank}
              onBlur={handleBlur}
              onChange={handleChange}
            >
              {quadRanks.map((rank) => <option key={rank} value={rank}>{rank}</option>)}
            </select>
            {touched.quad_of_day_rank && errors.quad_of_day_rank
              ? <small role="alert">{errors.quad_of_day_rank}</small>
              : null}
          </label>

          {downloadError ? <small role="alert">{downloadError}</small> : null}

          <div className="progressive-bonuses-editor__actions">
            <MDButton
              className="progressive-bonuses-editor__download"
              type="button"
              variant="outlined"
              color="secondary"
              disabled={isDownloading}
              onClick={() => handleDownload(values)}
              startIcon={isDownloading ? null : <DownloadIcon />}
            >
              {isDownloading ? (
                <>
                  <CircularProgress size={18} color="inherit" sx={{ marginRight: 1 }} />
                  Preparing image…
                </>
              ) : "Download image"}
            </MDButton>

            <MDButton
              className="progressive-bonuses-editor__save"
              type="submit"
              variant="gradient"
              color="secondary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={18} color="inherit" sx={{ marginRight: 1 }} />
                  Saving…
                </>
              ) : "Save bonuses"}
            </MDButton>
          </div>
        </Form>
      )}
    </Formik>
  );
}

ProgressiveBonusesEditorForm.propTypes = {
  bonuses: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
};

ProgressiveBonusesEditorForm.defaultProps = {
  onDownload: downloadProgressiveBonusesImage,
};

export default ProgressiveBonusesEditorForm;
